// Context provider that lets a module view publish content into the
// shell's right-rail ("context rail") on wide viewports, plus an
// optional page title and breadcrumb zone hint.
//
// Pattern: each module calls `useModuleSlot` and pushes an object
// describing its right-rail content. The shell reads from the same
// context and renders the rail only when the active module has
// supplied a node. On narrow viewports (< xl) the context is
// recorded but the rail is never rendered — the module is free to
// re-publish content via its own on-page layout for those widths.
//
// Using a single provider (rather than per-slot providers) keeps
// the API simple: one hook, one push, one render. The hook uses
// useSyncExternalStore semantics so the shell re-renders the moment
// the module changes; switching modules clears the rail cleanly.

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export type ModuleSlot = {
  /** Element rendered in the right-rail on `xl+`. Module decides
   *  the content. Shell owns the surrounding chrome. */
  context: ReactNode;
  /** Short page title (e.g. "FamList"). Renders in the desktop top
   *  bar breadcrumb zone. Optional; the shell falls back to the
   *  module's nav label. */
  pageTitle?: string;
  /** Optional secondary text in the breadcrumb (e.g. "清單 #3 —
   *  下週掃貨"). */
  pageSubtitle?: string;
};

type ModuleSlotState = ModuleSlot | null;

const ModuleSlotContext = createContext<{
  slot: ModuleSlotState;
  setSlot: (next: ModuleSlotState) => void;
} | null>(null);

export function ModuleSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<ModuleSlotState>(null);
  // Memoize the context value so consumers see the same object
  // reference across re-renders. Without this, every Provider
  // re-render creates a fresh `{slot, setSlot}` object, which
  // makes `ctx` in `usePublishModuleSlot` look unstable in its
  // `useEffect` deps and triggers the effect — which calls
  // `setSlot` — every render, producing "Maximum update depth
  // exceeded". (Caught in prod 2026-08-04.)
  const value = useMemo(() => ({ slot, setSlot }), [slot]);
  return (
    <ModuleSlotContext.Provider value={value}>
      {children}
    </ModuleSlotContext.Provider>
  );
}

/* Hook used by the active module view. The push is "idempotent"
 * in the sense that calling it on every render with the same
 * `next` is a no-op; the component that owns this hook decides
 * how often to call it (typically by wrapping the slot in
 * `useMemo([...deps])` so identity only changes when the
 * underlying data changes).
 *
 * Why `ctx` is NOT in the dep array: the Provider memoizes its
 * `value` prop (see ModuleSlotProvider above), so consumers see
 * the same object reference across renders. Including `ctx`
 * here is harmless when it's stable, but the previous version
 * depended on `ctx` changing every Provider render — a Provider
 * that re-created the value inline each render would feed a
 * `setSlot → Provider re-render → ctx changes → effect re-runs →
 * setSlot` loop that surfaced in prod as React's "Maximum update
 * depth exceeded" (caught 2026-08-04). Dropping `ctx` from the
 * deps removes that risk surface; the cost is a one-time
 * `eslint-disable react-hooks/exhaustive-deps` (the ref + setter
 * pair is genuinely stable). */
export function usePublishModuleSlot(next: ModuleSlotState) {
  const ctx = useContext(ModuleSlotContext);
  if (!ctx) {
    // Outside the provider (e.g. test renderer) — silently no-op
    // so module components don't crash in isolation.
    return;
  }
  const lastRef = useRef<ModuleSlotState>(null);
  const { setSlot } = ctx;
  useEffect(() => {
    if (lastRef.current !== next) {
      lastRef.current = next;
      setSlot(next);
    }
  }, [next, setSlot]);
}

export function useModuleSlot(): ModuleSlotState {
  const ctx = useContext(ModuleSlotContext);
  return ctx?.slot ?? null;
}
